import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api/client';
import { useOfflineCheckinQueue } from '../hooks/useOfflineCheckinQueue';
import { useIsMobile } from '../hooks/useIsMobile';

// ---- Shared Kotoku cache contract (kept identical so this station and the
// mobile Kotoku tool share ONE offline cache and check-in behavior). ----
const CACHE_KEY = 'kotoku_guest_data';
const CACHE_TS_KEY = 'kotoku_guest_data_ts';
const OFFLINE_MODE_KEY = 'kotoku_force_offline';
const PIN_KEY = 'kotoku_checkin_pin';
const LOCATION_KEY = 'kotoku_station_location';

// pickupLocation enum values understood by the backend (CheckIn.pickupLocation).
type PickupLocation = 'Hotel' | 'Symposium' | 'Meet_and_Greet' | 'Ball_Door';
const LOCATIONS: { value: PickupLocation; label: string }[] = [
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Symposium', label: 'Symposium' },
  { value: 'Meet_and_Greet', label: 'Meet & Greet' },
  { value: 'Ball_Door', label: 'Ball Door' },
];

// The two packet-delivery queues shown on the Delivery Board.
const DELIVERY_QUEUES = ['Deep Wata Quay', 'Govna Wharf'];
const QUEUE_ACCENTS: Record<string, string> = {
  'Deep Wata Quay': '#0369a1',
  'Govna Wharf': '#b45309',
};

function getStationLocation(): PickupLocation {
  const v = localStorage.getItem(LOCATION_KEY);
  if (v === 'Hotel' || v === 'Symposium' || v === 'Meet_and_Greet' || v === 'Ball_Door') return v;
  return 'Ball_Door';
}

const AUTO_CHECKIN_KEY = 'kotoku_station_auto_checkin';

// Guest QR codes encode JSON like { type: 'guest', identifier: <registrationId> }.
// A scanner "types" that whole string into the field; pull the id back out so we
// can match on registrationId rather than searching for raw JSON.
function extractRegistrationId(raw: string): string | null {
  const t = raw.trim();
  if (!t.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(t);
    if (parsed && typeof parsed.identifier === 'string') return parsed.identifier;
  } catch {
    // Not JSON — treat as a normal typed/scanned value.
  }
  return null;
}

interface GuestDirectoryResult {
  fullName: string;
  ticketNumber: string | null;
  tableNumber: number | null;
  tableName: string | null;
  seatNumber: number | null;
  zoneName: string | null;
  chapterAffiliation: string | null;
  orderReferenceId: string | null;
  registrationId?: string | null;
  isCheckedIn?: boolean;
  checkedInAt?: string | null;
  pickupLocation?: string | null;
  directions?: string | null;
  suggestedEntrance?: string | null;
}

function getCachedData(): GuestDirectoryResult[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getForceOffline(): boolean {
  return localStorage.getItem(OFFLINE_MODE_KEY) === 'true';
}

function localSearch(data: GuestDirectoryResult[], query: string): GuestDirectoryResult[] {
  const q = query.toLowerCase().trim();
  if (q.length < 2) return [];
  return data
    .filter((g) =>
      (g.fullName || '').toLowerCase().includes(q) ||
      (g.ticketNumber || '').toLowerCase().includes(q) ||
      (g.orderReferenceId || '').toLowerCase().includes(q) ||
      (g.chapterAffiliation || '').toLowerCase().includes(q) ||
      (g.tableName || '').toLowerCase().includes(q))
    .slice(0, 200);
}

export function CheckinStationPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { enqueueCheckin, pending, isOnline, syncing, isQueued, flush, removeCheckin } = useOfflineCheckinQueue();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GuestDirectoryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [cachedCount, setCachedCount] = useState(0);
  const [cacheTime, setCacheTime] = useState<string | null>(null);
  const [forceOffline, setForceOffline] = useState(getForceOffline());
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  const [selected, setSelected] = useState<GuestDirectoryResult | null>(null);
  const [flash, setFlash] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null);
  const [locallyCheckedIn, setLocallyCheckedIn] = useState<Set<string>>(new Set());
  const [showQueue, setShowQueue] = useState(false);
  const [location, setLocation] = useState<PickupLocation>(getStationLocation());
  const [queueCounts, setQueueCounts] = useState<Record<string, number>>({});
  const [autoCheckin, setAutoCheckin] = useState<boolean>(() => localStorage.getItem(AUTO_CHECKIN_KEY) === 'true');
  const searchRef = useRef<HTMLInputElement | null>(null);
  // When a submit resolves to exactly one guest, we stash a follow-up action
  // here (open the card, and optionally auto-check-in) to run once results land.
  const pendingActionRef = useRef<{ autoCheckin: boolean } | null>(null);

  const useOffline = forceOffline || !browserOnline;

  // Register the shared Kotoku service worker + connectivity listeners.
  useEffect(() => {
    document.title = 'Check-In Station';
    const cached = getCachedData();
    if (cached) setCachedCount(cached.length);
    setCacheTime(localStorage.getItem(CACHE_TS_KEY));

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/kotoku-sw.js').catch(() => {});
    }

    const onOnline = () => setBrowserOnline(true);
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    searchRef.current?.focus();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Auto-clear the flash message.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  // Poll the delivery queue counts (online only) so the operator can see the
  // packet-delivery workload building up as they check people in.
  useEffect(() => {
    if (useOffline) return;
    let cancelled = false;
    const load = async () => {
      try {
        // Public, auth-free counts endpoint (the station has no login token).
        const res = await fetch(`${API_BASE}/guest-directory/queue-counts`);
        if (!res.ok) return;
        const data: Record<string, number> = await res.json();
        if (cancelled || !data || typeof data !== 'object') return;
        const counts: Record<string, number> = {};
        for (const q of DELIVERY_QUEUES) counts[q] = Number(data[q]) || 0;
        setQueueCounts(counts);
      } catch {
        // Non-critical — leave last known counts.
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [useOffline]);

  const handleSync = useCallback(async () => {
    setSyncingData(true);
    setSyncError(null);
    try {
      const res = await fetch(`${API_BASE}/guest-directory/all`);
      if (!res.ok) throw new Error('Sync failed');
      const data: GuestDirectoryResult[] = await res.json();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      const ts = new Date().toLocaleString();
      localStorage.setItem(CACHE_TS_KEY, ts);
      setCachedCount(data.length);
      setCacheTime(ts);
      setFlash({ text: `Synced ${data.length} guests for offline use`, tone: 'ok' });
    } catch (err: any) {
      setSyncError(err?.message || 'Failed to sync guest data');
    } finally {
      setSyncingData(false);
    }
  }, []);

  const runSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setLoading(true);
    setSearched(true);
    setSelected(null);

    // A scanned guest QR is JSON with a registrationId — match on that directly.
    const scannedRegId = extractRegistrationId(trimmed);
    // Queue a follow-up: on a single-result resolve, open the card (and check in
    // if the operator has auto-check-in on). This fires from the effect below.
    pendingActionRef.current = { autoCheckin };

    if (scannedRegId) {
      const cached = getCachedData();
      const match = (cached || []).filter((g) => g.registrationId === scannedRegId);
      if (match.length > 0) {
        setResults(match);
        setLoading(false);
        return;
      }
      // Not in cache — fall through to normal search using the raw id string.
    }

    const q = scannedRegId || trimmed;
    if (useOffline) {
      const cached = getCachedData();
      setResults(cached ? localSearch(cached, q) : []);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/guest-directory/search?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error();
      const data: GuestDirectoryResult[] = await res.json();
      setResults(data);
    } catch {
      const cached = getCachedData();
      setResults(cached ? localSearch(cached, q) : []);
    } finally {
      setLoading(false);
    }
  }, [query, useOffline, autoCheckin]);

  const ensurePin = useCallback((): string | null => {
    let pin = localStorage.getItem(PIN_KEY) || '';
    if (!pin) {
      const entered = prompt('Enter check-in PIN:');
      if (!entered) return null;
      pin = entered;
      localStorage.setItem(PIN_KEY, pin);
    }
    return pin;
  }, []);

  const isGuestCheckedIn = useCallback(
    (g: GuestDirectoryResult): boolean => {
      const rid = g.registrationId || '';
      return Boolean(g.isCheckedIn) || locallyCheckedIn.has(rid) || (rid ? isQueued(rid) : false);
    },
    [locallyCheckedIn, isQueued],
  );

  const handleCheckIn = useCallback(
    async (g: GuestDirectoryResult) => {
      const rid = g.registrationId;
      if (!rid) {
        setFlash({ text: 'This guest has no registration linked — cannot check in.', tone: 'err' });
        return;
      }
      const pin = ensurePin();
      if (!pin) return;

      // OFFLINE (or forced): queue it and optimistically mark checked in.
      if (useOffline) {
        const added = enqueueCheckin({ registrationId: rid, guestName: g.fullName, ticketNumber: g.ticketNumber, pickupLocation: location, pin });
        setLocallyCheckedIn((prev) => new Set(prev).add(rid));
        setFlash({
          text: added
            ? `${g.fullName} checked in — queued, will sync when back online`
            : `${g.fullName} is already queued`,
          tone: 'warn',
        });
        return;
      }

      // ONLINE: send now.
      try {
        const res = await fetch(`${API_BASE}/guest-directory/check-in`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registrationId: rid, pickupLocation: location, pin }),
        });
        const result = await res.json();
        if (result?.success === false) {
          setFlash({ text: result.message || 'Check-in failed', tone: 'err' });
          return;
        }
        setLocallyCheckedIn((prev) => new Set(prev).add(rid));
        setResults((prev) => prev.map((x) => (x.registrationId === rid ? { ...x, isCheckedIn: true } : x)));
        setFlash({ text: result?.message || `✓ ${g.fullName} checked in`, tone: 'ok' });
      } catch {
        // Network dropped mid-request — fall back to the queue so the tap isn't lost.
        enqueueCheckin({ registrationId: rid, guestName: g.fullName, ticketNumber: g.ticketNumber, pickupLocation: location, pin });
        setLocallyCheckedIn((prev) => new Set(prev).add(rid));
        setFlash({ text: `Network issue — ${g.fullName} queued for sync`, tone: 'warn' });
      }
    },
    [useOffline, ensurePin, enqueueCheckin, location],
  );

  const handleUndoCheckIn = useCallback(
    async (g: GuestDirectoryResult) => {
      const rid = g.registrationId;
      if (!rid) return;
      if (!confirm(`Uncheck ${g.fullName}? This reverses their check-in.`)) return;

      // If it's only sitting in the offline queue (checked in this session, not
      // yet synced), just drop it from the queue — nothing hit the server yet.
      const queued = pending.find((c) => c.registrationId === rid);
      if (queued) {
        removeCheckin(queued.id);
        setLocallyCheckedIn((prev) => { const n = new Set(prev); n.delete(rid); return n; });
        setFlash({ text: `${g.fullName} removed from the sync queue`, tone: 'warn' });
        return;
      }

      // Undo requires the server (needs to delete the CheckIn record + reset seat).
      if (useOffline) {
        setFlash({ text: 'Uncheck needs a connection — reconnect and try again.', tone: 'err' });
        return;
      }
      const pin = ensurePin();
      if (!pin) return;
      try {
        const res = await fetch(`${API_BASE}/guest-directory/undo-check-in`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registrationId: rid, pin }),
        });
        const result = await res.json();
        if (result?.success === false) {
          setFlash({ text: result.message || 'Uncheck failed', tone: 'err' });
          return;
        }
        setLocallyCheckedIn((prev) => { const n = new Set(prev); n.delete(rid); return n; });
        setResults((prev) => prev.map((x) => (x.registrationId === rid ? { ...x, isCheckedIn: false } : x)));
        setSelected((prev) => (prev && prev.registrationId === rid ? { ...prev, isCheckedIn: false } : prev));
        setFlash({ text: result?.message || `↩ ${g.fullName} check-in reversed`, tone: 'ok' });
      } catch {
        setFlash({ text: 'Uncheck failed — network issue.', tone: 'err' });
      }
    },
    [pending, removeCheckin, useOffline, ensurePin],
  );

  const handleLocationChange = (value: PickupLocation) => {
    setLocation(value);
    localStorage.setItem(LOCATION_KEY, value);
  };

  const toggleAutoCheckin = () => {
    setAutoCheckin((prev) => {
      const next = !prev;
      localStorage.setItem(AUTO_CHECKIN_KEY, String(next));
      return next;
    });
  };

  // After a search/scan resolves, if there's a queued follow-up action and the
  // result is a single guest, open their card — and auto-check-in if enabled.
  useEffect(() => {
    if (loading) return;
    const action = pendingActionRef.current;
    if (!action) return;
    pendingActionRef.current = null;
    if (results.length === 1) {
      const only = results[0];
      setSelected(only);
      if (action.autoCheckin && !isGuestCheckedIn(only)) {
        handleCheckIn(only);
      }
    }
  }, [results, loading, isGuestCheckedIn, handleCheckIn]);

  // Keep the search box focused for hands-free scanning (scanner types + Enter).
  useEffect(() => {
    if (!selected && !loading) searchRef.current?.focus();
  }, [selected, loading, results]);

  const toggleForceOffline = () => {
    const next = !forceOffline;
    setForceOffline(next);
    localStorage.setItem(OFFLINE_MODE_KEY, String(next));
  };

  const modeLabel = useOffline ? (forceOffline ? 'OFFLINE (forced)' : 'OFFLINE') : 'ONLINE';

  const timeAgo = (ts: number): string => {
    const secs = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={isMobile ? { ...styles.header, flexWrap: 'wrap', gap: '0.5rem', padding: '0.75rem 1rem' } : styles.header}>
        <div style={isMobile ? { ...styles.headerLeft, flexWrap: 'wrap', gap: '0.5rem' } : styles.headerLeft}>
          <button onClick={() => navigate('/volunteer')} style={styles.backBtn} aria-label="Back to Volunteer Console">
            ← Back
          </button>
          <h1 style={styles.title}>🎟️ Check-In Station</h1>
          <label style={styles.locationWrap}>
            <span style={styles.locationLabel}>📍 Station</span>
            <select
              style={styles.locationSelect}
              value={location}
              onChange={(e) => handleLocationChange(e.target.value as PickupLocation)}
              aria-label="Station location"
            >
              {LOCATIONS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </label>
          <label style={styles.autoWrap} title="When on, a scan/search that finds one guest checks them in automatically">
            <input type="checkbox" checked={autoCheckin} onChange={toggleAutoCheckin} />
            <span style={styles.autoLabel}>⚡ Auto check-in on scan</span>
          </label>
        </div>
        <div style={styles.headerRight}>
          {pending.length > 0 && (
            <button
              style={styles.pendingPill}
              title="View check-ins waiting to sync"
              onClick={() => setShowQueue((s) => !s)}
              aria-expanded={showQueue}
            >
              {syncing ? '↻ ' : '⏳ '}
              {pending.length} pending sync
              <span style={styles.pendingCaret}>{showQueue ? '▲' : '▼'}</span>
            </button>
          )}
          <span style={{ ...styles.modePill, backgroundColor: useOffline ? '#7c2d12' : '#166534' }}>
            {useOffline ? '⚠ ' : '● '}
            {modeLabel}
          </span>
        </div>
      </header>

      {/* Pending-sync panel */}
      {showQueue && pending.length > 0 && (
        <div style={styles.queuePanel}>
          <div style={styles.queuePanelHead}>
            <span style={styles.queuePanelTitle}>
              ⏳ {pending.length} check-in{pending.length === 1 ? '' : 's'} waiting to sync
              {isOnline ? (syncing ? ' · syncing…' : ' · will sync shortly') : ' · offline'}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isOnline && (
                <button onClick={flush} style={styles.queueSyncBtn}>Sync now</button>
              )}
              <button onClick={() => setShowQueue(false)} style={styles.queueCloseBtn} aria-label="Close panel">✕</button>
            </div>
          </div>
          <div style={styles.queueList}>
            {pending.map((q) => (
              <div key={q.id} style={styles.queueRow}>
                <div style={styles.queueMain}>
                  <span style={styles.queueName}>{q.guestName}</span>
                  <span style={styles.queueMeta}>
                    {q.ticketNumber ? <span style={styles.queueTicket}>{q.ticketNumber}</span> : null}
                    <span> · queued {timeAgo(q.queuedAt)}</span>
                  </span>
                </div>
                <button
                  style={styles.queueRemoveBtn}
                  onClick={() => removeCheckin(q.id)}
                  title="Remove from queue (does not undo an already-synced check-in)"
                  aria-label={`Remove ${q.guestName} from sync queue`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delivery queue count strip (online only) */}
      {!useOffline && (
        <div style={styles.queueStrip} aria-label="Packet delivery queue counts">
          <span style={styles.queueStripLabel}>📦 Delivery queues:</span>
          {DELIVERY_QUEUES.map((q) => (
            <span key={q} style={{ ...styles.queueCountPill, backgroundColor: QUEUE_ACCENTS[q] || '#374151' }}>
              {q}: <strong>{queueCounts[q] ?? 0}</strong>
            </span>
          ))}
          <span style={styles.queueStripTotal}>
            {DELIVERY_QUEUES.reduce((sum, q) => sum + (queueCounts[q] || 0), 0)} waiting total
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button onClick={handleSync} disabled={syncingData} style={styles.syncBtn}>
          {syncingData ? 'Syncing…' : '⬇ Sync guest data'}
        </button>
        <span style={styles.cacheInfo}>
          {cachedCount > 0
            ? `${cachedCount} guests cached${cacheTime ? ` · last synced ${cacheTime}` : ''}`
            : 'No cached data yet — Sync while online first'}
        </span>
        {pending.length > 0 && isOnline && (
          <button onClick={flush} style={styles.flushBtn}>
            Sync {pending.length} now
          </button>
        )}
        <label style={styles.forceOfflineLabel}>
          <input type="checkbox" checked={forceOffline} onChange={toggleForceOffline} /> Force offline
        </label>
      </div>

      {syncError && <div style={styles.errorBar}>{syncError}</div>}
      {flash && (
        <div
          style={{
            ...styles.flash,
            backgroundColor: flash.tone === 'ok' ? '#dcfce7' : flash.tone === 'warn' ? '#fef3c7' : '#fee2e2',
            color: flash.tone === 'ok' ? '#166534' : flash.tone === 'warn' ? '#92400e' : '#991b1b',
            borderColor: flash.tone === 'ok' ? '#86efac' : flash.tone === 'warn' ? '#fcd34d' : '#fca5a5',
          }}
        >
          {flash.text}
        </div>
      )}

      {/* Two-pane body (stacks to one column on mobile) */}
      <div style={isMobile ? { ...styles.body, gridTemplateColumns: '1fr', gridAutoRows: 'min-content', overflowY: 'auto' } : styles.body}>
        {/* LEFT: search + results */}
        <div style={isMobile ? { ...styles.leftPane, borderRight: 'none', borderBottom: '1px solid #e5e7eb' } : styles.leftPane}>
          <div style={styles.searchRow}>
            <div style={styles.searchInputWrap}>
              <input
                ref={searchRef}
                style={styles.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder="Scan a QR or type name, ticket #, order #, chapter…"
                aria-label="Search guests"
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('');
                    setResults([]);
                    setSearched(false);
                    setSelected(null);
                    searchRef.current?.focus();
                  }}
                  style={styles.clearBtn}
                  aria-label="Clear search"
                  title="Clear"
                >
                  ✕
                </button>
              )}
            </div>
            <button onClick={runSearch} style={styles.searchBtn} disabled={query.trim().length < 2}>
              Search
            </button>
          </div>

          {searched && !loading && results.length > 0 && (
            <div style={styles.resultsCount}>
              {results.length} match{results.length === 1 ? '' : 'es'} — scroll to browse
            </div>
          )}
          <div style={styles.resultsList}>
            {loading ? (
              <div style={styles.hint}>Searching…</div>
            ) : !searched ? (
              <div style={styles.hint}>Search for a guest to check them in.</div>
            ) : results.length === 0 ? (
              <div style={styles.hint}>No matches. {useOffline && cachedCount === 0 ? 'No cached data — sync while online.' : ''}</div>
            ) : (
              results.map((g, i) => {
                const checked = isGuestCheckedIn(g);
                const isSel = selected?.registrationId === g.registrationId && selected?.ticketNumber === g.ticketNumber;
                return (
                  <button
                    key={`${g.registrationId || g.ticketNumber || g.fullName}-${i}`}
                    style={{ ...styles.resultRow, ...(isSel ? styles.resultRowActive : {}) }}
                    onClick={() => setSelected(g)}
                  >
                    <div style={styles.resultMain}>
                      <span style={styles.resultName}>{g.fullName}</span>
                      <span style={styles.resultMeta}>
                        {g.ticketNumber ? <span style={styles.resultTicket}>{g.ticketNumber}</span> : null}
                        {g.orderReferenceId ? <span> · 📦 {g.orderReferenceId}</span> : null}
                        {g.tableName || g.tableNumber != null ? (
                          <span> · {g.tableName || `Table ${g.tableNumber}`}</span>
                        ) : null}
                      </span>
                    </div>
                    {checked && <span style={styles.miniCheck}>✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT: selected guest card */}
        <div style={styles.rightPane}>
          {!selected ? (
            <div style={styles.emptyCard}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎟️</div>
              <div>Select a guest to view their card and check them in.</div>
            </div>
          ) : (
            <div style={styles.card}>
              <div style={styles.cardName}>{selected.fullName}</div>
              <div style={styles.cardChapter}>{selected.chapterAffiliation || '—'}</div>

              <div style={styles.pillRow}>
                {selected.tableNumber != null && (
                  <div style={{ ...styles.bigPill, backgroundColor: '#1a6b3c' }}>
                    <span style={styles.bigPillLabel}>TABLE</span>
                    <span style={styles.bigPillValue}>{selected.tableNumber}</span>
                  </div>
                )}
                {selected.seatNumber != null && (
                  <div style={{ ...styles.bigPill, backgroundColor: '#0d47a1' }}>
                    <span style={styles.bigPillLabel}>SEAT</span>
                    <span style={styles.bigPillValue}>{selected.seatNumber}</span>
                  </div>
                )}
                <div style={{ ...styles.bigPill, backgroundColor: '#7b1fa2' }}>
                  <span style={styles.bigPillLabel}>TICKET</span>
                  <span style={styles.bigPillValueSm}>{selected.ticketNumber || '—'}</span>
                </div>
                {selected.orderReferenceId && (
                  <div style={{ ...styles.bigPill, backgroundColor: '#b45309' }}>
                    <span style={styles.bigPillLabel}>ORDER</span>
                    <span style={styles.bigPillValueSm}>{selected.orderReferenceId}</span>
                  </div>
                )}
              </div>

              {selected.tableName && (
                <div style={styles.cardTableName}>{selected.tableName}</div>
              )}
              {selected.zoneName && (
                <div style={styles.cardLine}>Zone: {selected.zoneName}</div>
              )}

              {(selected.directions || selected.suggestedEntrance) && (
                <div style={styles.wayfind}>
                  🧭 {selected.directions || `Enter via ${selected.suggestedEntrance}`}
                </div>
              )}

              <div style={styles.statusLine}>
                {isGuestCheckedIn(selected) ? (
                  selected.registrationId && isQueued(selected.registrationId) ? (
                    <span style={{ color: '#92400e', fontWeight: 800 }}>⏳ Checked in — pending sync</span>
                  ) : (
                    <span style={{ color: '#16a34a', fontWeight: 800 }}>✅ Checked in</span>
                  )
                ) : (
                  <span style={{ color: '#d97706', fontWeight: 800 }}>⬜ Not checked in yet</span>
                )}
              </div>

              <button
                style={{
                  ...styles.checkInBtn,
                  ...(isGuestCheckedIn(selected) ? styles.checkInBtnDone : {}),
                }}
                onClick={() => handleCheckIn(selected)}
                disabled={isGuestCheckedIn(selected)}
              >
                {isGuestCheckedIn(selected)
                  ? 'Already Checked In'
                  : useOffline
                    ? `✓ Check In + Packet (${LOCATIONS.find((l) => l.value === location)?.label}) — offline`
                    : `✓ Check In + Packet (${LOCATIONS.find((l) => l.value === location)?.label})`}
              </button>

              {isGuestCheckedIn(selected) && (
                <button style={styles.uncheckBtn} onClick={() => handleUndoCheckIn(selected)}>
                  ↩ Uncheck Guest
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: 'fixed', inset: 0, backgroundColor: '#f5f7f6', color: '#0f2d1f',
    display: 'flex', flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', boxSizing: 'border-box',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.9rem 1.5rem', backgroundColor: '#0b1f16', color: '#fff', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '1rem' },
  backBtn: { backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '0.45rem 0.9rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' },
  title: { fontSize: '1.4rem', fontWeight: 900, margin: 0 },
  locationWrap: { display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.25rem 0.6rem' },
  locationLabel: { fontSize: '0.8rem', fontWeight: 700, color: '#a7f3d0', whiteSpace: 'nowrap' },
  locationSelect: { backgroundColor: '#fff', color: '#0f2d1f', border: 'none', borderRadius: '6px', padding: '0.35rem 0.5rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' },
  autoWrap: { display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '0.35rem 0.7rem', cursor: 'pointer' },
  autoLabel: { fontSize: '0.8rem', fontWeight: 700, color: '#fde68a', whiteSpace: 'nowrap' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  pendingPill: { fontSize: '0.85rem', fontWeight: 800, backgroundColor: '#b45309', color: '#fff', padding: '0.35rem 0.9rem', borderRadius: '999px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' },
  pendingCaret: { fontSize: '0.65rem' },
  queuePanel: { backgroundColor: '#fff7ed', borderBottom: '2px solid #fdba74', flexShrink: 0, maxHeight: '38vh', display: 'flex', flexDirection: 'column' },
  queuePanelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.7rem 1.5rem', borderBottom: '1px solid #fed7aa' },
  queuePanelTitle: { fontSize: '0.9rem', fontWeight: 800, color: '#9a3412' },
  queueSyncBtn: { backgroundColor: '#b45309', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.35rem 0.9rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
  queueCloseBtn: { background: 'none', border: 'none', color: '#9a3412', fontSize: '1rem', fontWeight: 900, cursor: 'pointer', padding: '0 0.4rem' },
  queueList: { overflowY: 'auto', padding: '0.4rem 1rem 0.8rem' },
  queueRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.5rem 0.6rem', borderBottom: '1px solid #fee2c8' },
  queueMain: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  queueName: { fontSize: '0.98rem', fontWeight: 700, color: '#7c2d12' },
  queueMeta: { fontSize: '0.78rem', color: '#b45309', marginTop: '1px' },
  queueTicket: { fontFamily: 'monospace', fontWeight: 700 },
  queueRemoveBtn: { flexShrink: 0, background: 'none', border: '1px solid #fdba74', color: '#9a3412', borderRadius: '8px', padding: '0.3rem 0.75rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' },
  modePill: { fontSize: '0.85rem', fontWeight: 800, color: '#fff', padding: '0.35rem 0.9rem', borderRadius: '999px' },
  queueStrip: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 1.5rem', backgroundColor: '#0b1f16', color: '#fff', flexShrink: 0, flexWrap: 'wrap' },
  queueStripLabel: { fontSize: '0.85rem', fontWeight: 800, color: '#a7f3d0' },
  queueCountPill: { fontSize: '0.95rem', fontWeight: 700, color: '#fff', padding: '0.3rem 0.9rem', borderRadius: '999px' },
  queueStripTotal: { marginLeft: 'auto', fontSize: '0.85rem', fontWeight: 700, color: '#a7f3d0' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1.5rem', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0, flexWrap: 'wrap' },
  syncBtn: { backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1.1rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' },
  flushBtn: { backgroundColor: '#b45309', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1.1rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' },
  cacheInfo: { fontSize: '0.82rem', color: '#6b7280' },
  forceOfflineLabel: { marginLeft: 'auto', fontSize: '0.82rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' },
  errorBar: { backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.6rem 1.5rem', fontSize: '0.9rem', flexShrink: 0 },
  flash: { padding: '0.7rem 1.5rem', fontSize: '0.95rem', fontWeight: 700, borderBottom: '1px solid', flexShrink: 0 },
  body: { flex: 1, display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', minHeight: 0 },
  leftPane: { display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb', backgroundColor: '#fff', minHeight: 0 },
  searchRow: { display: 'flex', gap: '0.5rem', padding: '1rem', borderBottom: '1px solid #f0f0f0' },
  searchInputWrap: { position: 'relative', flex: 1, display: 'flex', alignItems: 'center' },
  searchInput: { width: '100%', padding: '0.7rem 2.4rem 0.7rem 0.9rem', fontSize: '1rem', border: '2px solid #d1d5db', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' },
  clearBtn: { position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: '#e5e7eb', border: 'none', borderRadius: '50%', width: '1.5rem', height: '1.5rem', fontSize: '0.8rem', fontWeight: 900, color: '#374151', cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  searchBtn: { backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '10px', padding: '0 1.1rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' },
  resultsCount: { padding: '0.5rem 1rem', fontSize: '0.78rem', fontWeight: 700, color: '#6b7280', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  resultsList: { flex: 1, overflowY: 'auto', padding: '0.5rem' },
  hint: { color: '#9ca3af', fontSize: '0.95rem', textAlign: 'center', padding: '2.5rem 1rem' },
  resultRow: { width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', padding: '0.75rem 0.9rem', cursor: 'pointer', borderRadius: '8px' },
  resultRowActive: { backgroundColor: '#ecfdf5' },
  resultMain: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  resultName: { fontSize: '1.05rem', fontWeight: 700, color: '#0f2d1f' },
  resultMeta: { fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' },
  resultTicket: { fontFamily: 'monospace', color: '#1565c0', fontWeight: 700 },
  miniCheck: { color: '#16a34a', fontWeight: 900, fontSize: '1.2rem', flexShrink: 0 },
  rightPane: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem', overflowY: 'auto', minHeight: 0 },
  emptyCard: { color: '#9ca3af', fontSize: '1.05rem', textAlign: 'center', marginTop: '4rem' },
  card: { width: '100%', maxWidth: '560px', backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.12)', padding: '2rem' },
  cardName: { fontSize: '2rem', fontWeight: 900, color: '#0f2d1f', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardChapter: { fontSize: '1rem', color: '#374151', marginTop: '0.25rem', marginBottom: '1.5rem' },
  pillRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' },
  bigPill: { flex: '1 1 120px', color: '#fff', borderRadius: '16px', padding: '1rem', textAlign: 'center', lineHeight: 1.1 },
  bigPillLabel: { display: 'block', fontSize: '0.75rem', fontWeight: 700, opacity: 0.85 },
  bigPillValue: { display: 'block', fontSize: '2.5rem', fontWeight: 900 },
  bigPillValueSm: { display: 'block', fontSize: '1.4rem', fontWeight: 900 },
  cardTableName: { fontSize: '2rem', fontWeight: 900, color: '#0f2d1f', textTransform: 'uppercase', letterSpacing: '0.01em', marginBottom: '0.35rem', lineHeight: 1.1 },
  cardLine: { fontSize: '1rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '0.75rem' },
  wayfind: { backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '1rem' },
  statusLine: { fontSize: '1.05rem', marginBottom: '1.25rem' },
  checkInBtn: { width: '100%', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '14px', padding: '1.1rem', fontSize: '1.3rem', fontWeight: 900, cursor: 'pointer' },
  checkInBtnDone: { backgroundColor: '#9ca3af', cursor: 'default' },
  uncheckBtn: { width: '100%', marginTop: '0.75rem', backgroundColor: '#fff', color: '#b91c1c', border: '2px solid #fca5a5', borderRadius: '12px', padding: '0.85rem', fontSize: '1rem', fontWeight: 800, cursor: 'pointer' },
};
